import type { FormDataConvertible } from "@inertiajs/core";
import { router } from "@inertiajs/react";
import type { WizardRoutes } from "@/lib/wizard/types";

interface UseWizardStageProps<TData extends Record<string, FormDataConvertible>> {
  routes: WizardRoutes;
  stageName: string;
  stageIndex: number;
  stageOrder: string[];
  form: {
    patch: (url: string) => void;
    data: TData;
    processing: boolean;
  };
}

export interface UseWizardStageReturn {
  currentStage: number;
  totalStages: number;
  onBack: (() => void) | undefined;
  onPrimary: () => void;
  primaryPending: boolean;
  onPause: (() => void) | undefined;
}

/**
 * Resolves wizard navigation + submission props for a stage page. Returns values
 * that map directly onto the `Wizard` system component's props.
 *
 * `onBack` is `undefined` on the first stage (no previous stage and the entry
 * page is the back target only when explicit — callers can override). The hook
 * always routes via `router.get(backUrl)` to keep Product/Service consistent
 * with the rest of the creators (no `window.history.back()`).
 */
export function useWizardStage<TData extends Record<string, FormDataConvertible>>({
  routes,
  stageName,
  stageIndex,
  stageOrder,
  form,
}: UseWizardStageProps<TData>): UseWizardStageReturn {
  const previousStage = stageIndex > 0 ? stageOrder[stageIndex - 1] : null;
  const stageUrl = routes.stageUrl(stageName);
  const backUrl = previousStage ? routes.stageUrl(previousStage) : routes.entryUrl;
  const { pauseUrl } = routes;
  const hasBack = stageIndex > 0 || routes.entryUrl !== stageUrl;

  return {
    currentStage: stageIndex + 1,
    totalStages: stageOrder.length,
    onBack: hasBack ? () => router.get(backUrl) : undefined,
    onPrimary: () => form.patch(stageUrl),
    primaryPending: form.processing,
    onPause: pauseUrl ? () => router.post(pauseUrl, form.data) : undefined,
  };
}
